'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * Custom hook to block client-side navigation (via router.push)
 * when there are unsaved changes.
 * This hook DOES NOT handle browser back/forward buttons, page refresh, or closing the tab.
 * @param shouldBlock - boolean, indicates if navigation should be blocked.
 * @param checkUnsavedChanges - function to check if there are unsaved changes.
 */
export const useBlockNavigation = (
  shouldBlock: boolean,
  checkUnsavedChanges: (actionOnDiscard: () => void) => boolean
) => {
  const router = useRouter();

  const originalPushRef = useRef(router.push);

  useEffect(() => {
    const capturedOriginalPush = originalPushRef.current;
    const newPush = (href: string, options?: Parameters<typeof router.push>[1]) => {
      if (shouldBlock) {
        checkUnsavedChanges(() => {
          capturedOriginalPush(href, options);
        });
      } else {
        capturedOriginalPush(href, options);
      }
    };

    router.push = newPush as typeof router.push;

    return () => {
      router.push = capturedOriginalPush;
    };
  }, [router, shouldBlock, checkUnsavedChanges]);
};